'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BackButton } from '@/components/ui/BackButton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

import { useUpdateStripeProcessor, useDisconnectStripe, useInitiateStripeConnect } from '@/lib/api/stripe';
import { useStripeAccountDetails } from '@/lib/api/stripe';
import { ProcessorDetails, useProcessorById } from '@/lib/api/processors';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StripeEditSkeleton } from '../components/stripe-edit-skeleton';
import { config } from '@/lib/config';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { ProcessorApmSettings, ApmSettings, initApmSettingsFromOptions } from '../../components/processor-apm-settings';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';

interface StripeEditPageProps {
  processor: ProcessorDetails;
}

const getAccountId = (options: Record<string, unknown> | null) => {
  return options?.accountId as string | undefined;
};

const StripeEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return <StripeEditSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <StripeEditPage processor={data} />;
};

const StripeEditPage = ({ processor }: StripeEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = getAccountId(processor.options);
  const { orgRoutes } = useOrgNavigate();
  const { toast } = useToast();
  const processorExit = useProcessorEditExit();

  const [validationError, setValidationError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Handle URL parameters from Stripe OAuth callback
  useEffect(() => {
    const error = searchParams.get('error');
    const success = searchParams.get('success');
    const conflictProcessorId = searchParams.get('conflictProcessorId');
    const conflictProcessorName = searchParams.get('conflictProcessorName');

    if (error) {
      let errorMessage: string;

      if (error === 'stripe_already_connected' && conflictProcessorId) {
        // Show specific message with link to the conflicting processor
        errorMessage = t('stripeAlreadyConnectedTo', {
          processorName: conflictProcessorName || conflictProcessorId,
        });
      } else {
        // Map other error codes to user-friendly messages
        const errorMessages: Record<string, string> = {
          stripe_already_connected: t('stripeAlreadyConnected'),
          connection_failed: t('connectionFailed'),
          invalid_state: t('invalidState'),
          stripe_oauth_failed: t('stripeOauthFailed'),
          invalid_callback: t('invalidCallback'),
        };
        errorMessage = errorMessages[error] || t('connectionFailed');
      }

      setValidationError(errorMessage);

      // Clean up URL params
      searchParams.delete('error');
      searchParams.delete('conflictProcessorId');
      searchParams.delete('conflictProcessorName');
      setSearchParams(searchParams, { replace: true });
    }

    if (success) {
      toast({ description: t('stripeConnectedSuccessfully') });

      // Clean up URL params
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, t, toast]);

  const { data: stripeAccount, isLoading: isLoadingStripe } = useStripeAccountDetails(accountId || '');
  const updateProcessorMutation = useUpdateStripeProcessor();
  const disconnectMutation = useDisconnectStripe();
  const initiateConnectMutation = useInitiateStripeConnect();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    testMode: processor.options?.testMode ?? false,
    sendReceiptEmail: processor.options?.sendReceiptEmail ?? false,
    descriptorSuffix: processor.options?.descriptor?.descriptor_suffix || '',
    radarEnabled: processor.options?.radar?.enabled ?? false,
    radarPublishableKey: processor.options?.radar?.publishableKey || '',
    dryMode: (processor.options as any)?.dryMode === true,
  });

  const [apmSettings, setApmSettings] = useState<ApmSettings>(() =>
    initApmSettingsFromOptions('stripe', processor.options?.apmSettings as Record<string, Record<string, unknown>> | undefined),
  );
  const [expressPublishableKey, setExpressPublishableKey] = useState<string>(
    (processor.options as any)?.expressPublishableKey || '',
  );

  const handleConnectStripe = async () => {
    setIsConnecting(true);
    try {
      // Pass current URL as redirect destination after Stripe OAuth
      const currentUrl = window.location.href;
      const result = await initiateConnectMutation.mutateAsync({
        processorId: processor.id,
        returnUrl: `${config.apiBaseUrl}/api/public/oauth/stripe/callback`,
        isFromOnboarding: false,
        redirectUrl: currentUrl,
      });

      if (result?.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Stripe connection error:', error);
      setValidationError(t('failedToInitiateConnection'));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!accountId) {
      setValidationError(t('noStripeAccountConnected'));
      return;
    }

    setIsConnecting(true);
    try {
      await disconnectMutation.mutateAsync({
        processorId: processor.id,
        accountId,
      });

      toast({ description: t('disconnectedSuccessfully') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Stripe disconnect error:', error);
      setValidationError(t('failedToDisconnect'));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        data: {
          name: formData.name,
          enabled: formData.enabled,
          testMode: formData.testMode,
          sendReceiptEmail: formData.sendReceiptEmail,
          descriptor: {
            descriptor_suffix: formData.descriptorSuffix,
          },
          radar: {
            enabled: formData.radarEnabled,
            publishableKey: formData.radarPublishableKey,
          },
          apmSettings,
          expressPublishableKey: expressPublishableKey.trim() || undefined,
          dryMode: formData.dryMode,
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {processorExit.isEmbed ? (
            <Button variant="outline" size="icon" onClick={processorExit.exitCancel}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          ) : (
            <BackButton to={orgRoutes.PROCESSOR_DETAILS(processor.id)} />
          )}
          <h1 className="text-3xl font-bold">
            {accountId ? t('editStripeConfiguration') : t('connectStripeAccount')}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
          >
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={updateProcessorMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            {updateProcessorMutation.isPending ? t('saving') : t('saveChanges')}
          </Button>
        </div>
      </div>

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="configuration">
        <TabsList className="mb-6">
          <TabsTrigger value="configuration">{t('stripe.tabs.configuration')}</TabsTrigger>
          <TabsTrigger value="payment-methods">{t('stripe.tabs.paymentMethods')}</TabsTrigger>
        </TabsList>

        <TabsContent value="configuration">
      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Stripe Connection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('stripeConnection')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4 text-sm">{t('stripeConnectionDescription')}</p>

            {accountId ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <p className="text-muted-foreground text-sm">{t('accountId')}:</p>
                  <Badge variant="default">{accountId}</Badge>
                </div>
                <Button
                  onClick={handleDisconnect}
                  variant="destructive"
                  disabled={isConnecting}
                  className="w-full"
                >
                  {isConnecting ? t('disconnecting') : t('disconnectStripe')}
                </Button>
              </div>
            ) : (
              <Button onClick={handleConnectStripe} disabled={isConnecting} className="w-full">
                {isConnecting ? t('connecting') : t('connectWithStripe')}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Test Mode Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            {formData.testMode && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  {t('testModeDescription')}{' '}
                  <a
                    href="https://stripe.com/docs/testing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-900 underline hover:text-yellow-950"
                  >
                    {t('viewTestCards')}
                  </a>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('notificationSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendReceiptEmail"
                checked={formData.sendReceiptEmail}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, sendReceiptEmail: checked === true })
                }
              />
              <Label htmlFor="sendReceiptEmail">{t('sendReceiptEmail')}</Label>
            </div>
            {formData.sendReceiptEmail && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-800">{t('receiptEmailDescription')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statement Descriptor */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('statementDescriptor')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingStripe ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 w-3/4 rounded bg-gray-200"></div>
                <div className="h-4 w-1/2 rounded bg-gray-200"></div>
              </div>
            ) : stripeAccount?.settings?.payments?.statement_descriptor ? (
              <div className="bg-muted space-y-3 rounded-lg p-4">
                <div className="space-y-2">
                  <div>
                    <p className="text-muted-foreground text-sm">{t('accountDefaultDescriptor')}</p>
                    <p className="font-semibold">{stripeAccount.settings.payments.statement_descriptor}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">{t('shortenedDescriptor')}</p>
                    <p className="font-semibold">
                      {stripeAccount.settings.payments.statement_descriptor.substring(0, 10)}
                    </p>
                  </div>
                </div>
                <div className="text-muted-foreground space-y-2 text-xs">
                  <p>
                    {t('modifyDescriptorInDashboard')}{' '}
                    <a
                      href="https://dashboard.stripe.com/settings/public"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 underline"
                    >
                      {t('stripeDashboardSettings')}
                    </a>
                  </p>
                  <p className="font-medium">{t('requirementsForDefaultDescriptor')}</p>
                  <ul className="list-disc space-y-1 pl-4">
                    <li>{t('descriptorRequirement1')}</li>
                    <li>{t('descriptorRequirement2')}</li>
                    <li>{t('descriptorRequirement3')}</li>
                    <li>{t('descriptorRequirement4')}</li>
                    <li>{t('descriptorRequirement5')}</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  {t('configureDefaultDescriptor')}{' '}
                  <a
                    href="https://dashboard.stripe.com/settings/public"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-900 underline hover:text-yellow-950"
                  >
                    {t('stripeDashboardSettings')}
                  </a>
                </p>
              </div>
            )}

            {/* Suffix Input */}
            <div>
              <Label htmlFor="descriptor_suffix">{t('statementDescriptorSuffix')}</Label>
              <Input
                id="descriptor_suffix"
                value={formData.descriptorSuffix}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, descriptorSuffix: value });
                }}
                maxLength={12}
                placeholder={t('suffixPlaceholder')}
              />
              <div className="mt-2 space-y-2">
                <div className="text-muted-foreground space-y-1 text-xs">
                  <p>{t('suffixDescription')}</p>
                  <p className="font-medium">{t('requirementsForSuffix')}</p>
                  <ul className="list-disc space-y-1 pl-4">
                    <li>{t('suffixRequirement1')}</li>
                    <li>{t('suffixRequirement2')}</li>
                    <li>{t('suffixRequirement3')}</li>
                    <li>{t('suffixRequirement4')}</li>
                    <li>{t('suffixRequirement5')}</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">{t('exampleStatement')} </span>
                    {stripeAccount?.settings?.payments?.statement_descriptor
                      ? `${stripeAccount.settings.payments.statement_descriptor.substring(0, 10)}*${formData.descriptorSuffix || 'SUFFIX'}`
                      : 'YOUR_BIZ*SUFFIX'}
                  </p>
                  <p className="mt-1 text-xs text-blue-700">{t('totalLengthNote')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stripe Radar Fraud Protection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('stripeRadarFraudProtection')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="radarEnabled"
                checked={formData.radarEnabled}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, radarEnabled: checked === true })
                }
              />
              <Label htmlFor="radarEnabled">{t('enableStripeRadar')}</Label>
            </div>

            {formData.radarEnabled && (
              <div className="space-y-4">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-sm text-blue-800">
                    {t('stripeRadarEnabledDescription')}
                  </p>
                </div>

                <div>
                  <Label htmlFor="radarPublishableKey">{t('radarPublishableKey')}</Label>
                  <Input
                    id="radarPublishableKey"
                    value={formData.radarPublishableKey}
                    onChange={(e) =>
                      setFormData({ ...formData, radarPublishableKey: e.target.value })
                    }
                    placeholder="pk_live_... or pk_test_..."
                  />
                  <div className="text-muted-foreground mt-2 space-y-2 text-xs">
                    <p>
                      {t('radarKeyDescription')}{' '}
                      <a
                        href="https://dashboard.stripe.com/apikeys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline"
                      >
                        {t('stripeDashboard')}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-sm font-medium text-blue-800">
                    {t('stripeRadarFeatures')}
                  </p>
                  <ul className="mt-1 list-disc pl-4 text-xs text-blue-700">
                    <li>{t('radarFeature1')}</li>
                    <li>{t('radarFeature2')}</li>
                    <li>{t('radarFeature3')}</li>
                    <li>{t('radarFeature4')}</li>
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle>{t('featuresEnabled')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-muted-foreground list-disc space-y-1 pl-4 text-sm">
              <li>{t('featureDirectPayment')}</li>
              <li>{t('featureAutomaticPayouts')}</li>
              <li>
                {t('featurePaymentMethods')}
                <ul className="mt-1 list-disc pl-4">
                  <li>{t('featureCreditCards')}</li>
                  <li>{t('featureDigitalWallets')}</li>
                  <li>{t('featureLocalPayments')}</li>
                </ul>
              </li>
              <li>{t('featureFinancialReporting')}</li>
              {formData.radarEnabled && <li>{t('featureStripeRadar')}</li>}
            </ul>
          </CardContent>
        </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
      </div>
        </TabsContent>

        <TabsContent value="payment-methods">
          <ProcessorApmSettings
            processorType="stripe"
            processorId={processor.id}
            value={apmSettings}
            onChange={setApmSettings}
            expressPublishableKey={expressPublishableKey}
            onExpressPublishableKeyChange={setExpressPublishableKey}
            disabled={updateProcessorMutation.isPending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export const StripeEditPageContainer = withRequiredPermission(StripeEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
